/**
 * AI Workflow Quick Modal Component
 *
 * Simplified modal for creating workflows from the canvas.
 * Shows a compact form with just the essentials:
 * - Task description
 * - Industry selector
 * - Quick submit
 */

import React, { useState } from 'react';
import {
  X,
  Sparkles,
  Briefcase,
  AlertCircle,
  Loader
} from 'lucide-react';

const INDUSTRIES = [
  { value: 'healthcare', label: 'Healthcare' },
  { value: 'legal', label: 'Legal' },
  { value: 'finance', label: 'Finance' },
  { value: 'technology', label: 'Technology' },
  { value: 'retail', label: 'Retail' },
  { value: 'manufacturing', label: 'Manufacturing' },
  { value: 'education', label: 'Education' },
  { value: 'marketing', label: 'Marketing' },
  { value: 'real_estate', label: 'Real Estate' },
  { value: 'consulting', label: 'Consulting' },
  { value: 'government', label: 'Government' },
  { value: 'nonprofit', label: 'Non-Profit' },
  { value: 'other', label: 'Other' }
];

/**
 * Quick workflow modal
 */
export default function AIWorkflowQuickModal({ show, onClose, onSubmit, initialDescription = '' }) {
  const [taskDescription, setTaskDescription] = useState('');
  const [industry, setIndustry] = useState('other');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Update task description when initialDescription changes
  React.useEffect(() => {
    if (initialDescription) {
      setTaskDescription(initialDescription);
    }
  }, [initialDescription]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!taskDescription.trim()) {
      setError('Please enter a task description');
      return;
    }

    if (taskDescription.trim().length < 10) {
      setError('Task description must be at least 10 characters');
      return;
    }

    if (!industry) {
      setError('Please select an industry');
      return;
    }

    setLoading(true);

    try {
      await onSubmit({
        taskDescription: taskDescription.trim(),
        domain: industry
      });
      handleClose();
    } catch (err) {
      setError(err.message || 'Failed to create workflow');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setTaskDescription('');
    setIndustry('other');
    setError('');
    setLoading(false);
    onClose();
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black bg-opacity-50"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 p-6 z-10">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Sparkles size={24} className="text-blue-600" />
            <h2 className="text-xl font-bold text-gray-900">Quick AI Workflow</h2>
          </div>
          <button
            onClick={handleClose}
            className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
            disabled={loading}
          >
            <X size={20} className="text-gray-500" />
          </button>
        </div>

        <p className="text-sm text-gray-600 mb-4">
          Describe your task and we'll break it down into an intelligent workflow
        </p>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Task Description */}
          <div>
            <label htmlFor="taskDescription" className="block text-sm font-medium text-gray-700 mb-2">
              Task Description <span className="text-red-500">*</span>
            </label>
            <textarea
              id="taskDescription"
              value={taskDescription}
              onChange={(e) => setTaskDescription(e.target.value)}
              placeholder="Describe what you want to accomplish..."
              rows={4}
              disabled={loading}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none disabled:opacity-50"
            />
            <div className="mt-1 text-xs text-gray-500">
              {taskDescription.length} characters (minimum 10)
            </div>
          </div>

          {/* Industry Selector */}
          <div>
            <label htmlFor="industry" className="block text-sm font-medium text-gray-700 mb-2">
              Industry <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <Briefcase size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <select
                id="industry"
                value={industry}
                onChange={(e) => setIndustry(e.target.value)}
                disabled={loading}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50"
              >
                {INDUSTRIES.map((ind) => (
                  <option key={ind.value} value={ind.value}>
                    {ind.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Error message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2 text-red-700">
              <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          {/* Buttons */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={handleClose}
              disabled={loading}
              className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 font-medium rounded-lg hover:bg-gray-300 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader size={16} className="animate-spin" />
                  <span>Creating...</span>
                </>
              ) : (
                <>
                  <Sparkles size={16} />
                  <span>Create Workflow</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
